import { useEffect, useState } from 'react';
import { BigButton } from '../../common';
import { ModalArea } from '../../common/ModalButtonArea';
import { OrganizationCard } from '../../common/OrganizationSwitcher';
import { useUserContext } from '../../contexts/UserContext';
import { apiCreateOrganization, apiGetAllMyOrgs } from '../../helpers/api';
import styles from './OrganizationSelect.module.scss';
import { Col, Form, Row } from 'react-bootstrap';
import { errorToast, successToast, warningToast } from '../../helpers/toastHelper';
import { useCountryNameBasedOnTimezone } from '../../hooks/useCountryNameBasedOnTimezone';
import Button from '../Button';
import PlanDetails from '../../screens/subscriptionComponents/PlanDetails';
import { Mixpanel, MxEvent } from '../../common/Mixpanel';
import { useSwitchToOrganization } from '../../hooks/useSwitchToOrganization';
import FeatureItem from '../../screens/subscriptionComponents/FeatureItem';
import { useLocation } from 'react-router-dom';
import {
  AssignPackToOrg,
  getPackProductFeatureList,
  apiGetPackProductList,
} from '../../api/subscription/subscription';
import { HIDE_ASSIGN_PACKS_MODAL } from '../../constants/constants';
import { format } from 'date-fns';
import { fetchAllPages } from '../../helpers/apiHelper';
import { navigateToNewRequest } from '../../helpers/jsHelper';

export const OrganizationSelectModal = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const location = useLocation();
  const { isGuestUser } = useUserContext();
  const isWelcome = searchParams.get('welcome') === 'true';

  const shouldHide = () => HIDE_ASSIGN_PACKS_MODAL.some((url) => location.pathname.includes(url));
  if (shouldHide() || isGuestUser() || isWelcome) return null;
  return <OrganizationSelectModalUI />;
};

const OrganizationSelectModalUI = () => {
  const { switchToOrganization } = useSwitchToOrganization();
  const { currentUser, updateUserData, showAssignPackModal, setShowAssignPackModal } =
    useUserContext();
  const id = currentUser?.id;
  const organization_detail = currentUser?.organization_detail;
  const name = organization_detail?.name;
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [newOrgName, setNewOrgName] = useState('');
  const [saving, setSaving] = useState(false);
  const [packsLoading, setPacksLoading] = useState(false);
  const [showInput, setShowInput] = useState(false);
  // const [show, setShow] = useState(false);
  const [packList, setPackList] = useState([]);
  const [purchaseData, setPurchaseData] = useState([]);
  const [btnLoader, setBtnLoader] = useState(false);
  const [orgList, setOrgList] = useState(null);
  const hideAccordion = packList?.length < 2;
  const { countryName } = useCountryNameBasedOnTimezone();
  const { results } = orgList || {};

  useEffect(() => {
    if (currentUser) {
      getPackProduct();
    }
  }, [currentUser?.user?.id]);

  useEffect(() => {
    if (purchaseData?.length) {
      handleFilterRecord(purchaseData);
      getAllOrgList();
    }
  }, [purchaseData]);

  useEffect(() => {
    setSelectedOrg(organization_detail);
  }, [name]);

  const getAllOrgList = async () => {
    const res = await fetchAllPages((page) => {
      let param = { per_page: 20, page };
      return apiGetAllMyOrgs(param);
    });
    setOrgList(res || {});
  };

  const getPackProduct = async () => {
    const res = await apiGetPackProductList();
    if (res?.results) {
      setPurchaseData(res?.results);
    }
  };

  const handleFilterRecord = async (value) => {
    if (value?.length === 0) {
      setShowAssignPackModal(false);
      setPackList([]);
      return;
    }

    const groupByInvoiceId = (data) => {
      return data.reduce((result, item) => {
        const invoiceId = item.invoice_id;
        if (!result[invoiceId]) {
          result[invoiceId] = [];
        }
        result[invoiceId].push(item);
        return result;
      }, {});
    };

    const purchaseRecord = groupByInvoiceId(value);
    let filterRecord = {};
    Object.keys(purchaseRecord)?.map((key) => {
      filterRecord[key] = [];
    });
    setPacksLoading(true);
    await Promise.all(
      Object.keys(purchaseRecord)?.map(async (key) => {
        await Promise.all(
          purchaseRecord[key]?.map(async (pack, index) => {
            let list = filterRecord[key];
            list.push({
              ...pack,
              id: pack?.id,
              name: pack?.price?.nickname || pack?.product_name,
              features: !pack?.product
                ? []
                : (
                    await getPackProductFeatureList({
                      packProductId: pack?.product,
                    })
                  )?.results || [],
            });
            filterRecord[key] = list;
          })
        );
      })
    );
    setPacksLoading(false);

    const finalResult = Object.keys(filterRecord)?.map((key, index) => {
      return {
        name: `Purchased on ${format(
          new Date(filterRecord[key]?.[0]?.created_at),
          'MM-dd-yyyy'
        )} at ${format(new Date(filterRecord[key]?.[0]?.created_at), 'hh:mma')}
        `,
        expand: index === 0 ? true : false,
        childData: filterRecord[key].sort((a, b) => a.product_link_number - b.product_link_number),
        addOnsData: filterRecord[key].map((a) => ({
          [a.id]: a.add_on_data.map((b) => ({
            id: b.id,
            description: `${b.quantity} ${b.product_name}`,
          })),
        })),
        id: key,
      };
    });
    setPackList(finalResult || []);
    setShowAssignPackModal(true);
  };

  function handleSwitchOrg() {
    Mixpanel.track(true, MxEvent.CHANGED_ORGANIZATION, {
      organization_old_id: currentUser?.organization_detail?.id,
      organization_old_name: currentUser?.organization_detail?.name,
      organization_old_subscription_type: currentUser?.organization_detail?.subscription_type,
      organization_old_subscription_status_id: currentUser?.organization_detail?.status?.id,
      organization_old_subscription_status_name: currentUser?.organization_detail?.status?.name,
      organization_new_id: selectedOrg?.id,
      organization_new_name_new: selectedOrg?.name,
    });
    window.$recentProject = null;
    switchToOrganization(selectedOrg?.id, {
      onSuccess: () => {
        console.log('success');
      },
    });
  }

  const assignPackProductToOrg = async (payload) => {
    if (payload && !btnLoader) {
      setBtnLoader(true);
      const res = await AssignPackToOrg(payload);
      setBtnLoader(false);
      updateUserData();
      if (res?.status) {
        successToast(res?.message);
        if (+selectedOrg?.id !== +organization_detail?.id) {
          handleSwitchOrg();
        }
        packList?.length === 1 && setShowAssignPackModal(false);
        if (packList?.length === 1) {
          const pack = packList?.[0]?.childData?.[0];
          setTimeout(() => {
            navigateToNewRequest({
              purchase_id: pack?.id,
              seq: pack?.product_link_number,
              is_existing_model: pack?.price?.is_existing_model,
            })
            setPurchaseData([]);
            setPackList([]);
          }, 1500);
        } else {
          await getPackProduct();
        }
      } else {
        errorToast(res?.message || 'Something went wrong!');
      }
    }
  };

  const handleOrgClick = (orgId, orgName) => {
    setSelectedOrg({ id: orgId, name: orgName });
  };

  const onProceed = (value) => {
    if (value?.id) {
      const payload = {
        invoice_id: value?.id,
        organization: selectedOrg?.id,
      };
      assignPackProductToOrg(payload);
    }
  };

  const handleCancel = () => {
    setShowInput(false);
  };

  const handleSave = async () => {
    if (!newOrgName) {
      warningToast('Please provide the organization name.');
      return;
    }
    setSaving(true);
    await apiCreateOrganization(newOrgName, id, countryName);
    getAllOrgList();
    setShowInput(false);
    setNewOrgName('');
    setSaving(false);
  };

  const handleExpand = (value) => {
    let clonePack = packList.slice();
    clonePack.map((data) => {
      if (data?.id?.toString() == value?.id?.toString()) {
        data.expand = !data.expand;
      } else {
        data.expand = false;
      }
    });
    setPackList(clonePack);
  };

  const isDisabled = saving || packsLoading || btnLoader;

  if (!currentUser) return null;
  return (
    <ModalArea
      showModal={showAssignPackModal && orgList}
      setShowModal={() => setShowAssignPackModal(false)}
      title="Assign Packs to Organization"
      subtitle={''}
      size="lg"
      parentClassName="assignPackModal"
      centered={true}
      backdrop="static"
      hideCloseButton={true}
      body={
        <div className={`organisation ${styles.parentContainer}`}>
          {packList?.map((info) => {
            return (
              <PlanDetails
                key={info.id}
                title={info?.name}
                titleSectionStyle={{
                  paddingLeft: 0,
                  paddingRight: 0,
                  borderTop: 'none',
                  borderLeft: 'none',
                  borderRight: 'none',
                }}
                hideAccordion={hideAccordion}
                makeTitleBold={true}
                pointsClassName={'px-0 pack-link-box'}
                keepDetailsOpen={info?.expand}
                hideActions={true}
                handleExpand={() => handleExpand(info)}>
                {info?.childData && info?.childData?.length ? (
                  <div className="w-100">
                    <Row>
                      {info?.childData?.map((child, index) => {
                        return (
                          <Col key={child?.id} md={info?.childData?.length === 1 ? 12 : 6}>
                            <h6 className="org-title mt-2">
                              {child?.name} (Product {child?.product_link_number})
                            </h6>
                            <ul className="m-0 p-0">
                              {child?.features.map((a) => (
                                <FeatureItem key={a.id} title={a.description} />
                              ))}
                              {!child?.features?.length && (
                                <span>The feature has not been added yet.</span>
                              )}
                            </ul>
                            {info?.addOnsData?.[index]?.[child?.id]?.length > 0 ? (
                              <div
                                style={{
                                  margin: '0.5rem 0',
                                  width: '100%',
                                  padding: '0.5rem',
                                  background: '#F7F6F4',
                                }}>
                                <h6
                                  style={{
                                    marginBottom: 6,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: 'var(--primary-color)',
                                  }}>
                                  ADD-ONS
                                </h6>
                                <p
                                  style={{
                                    margin: 0,
                                    fontSize: 14,
                                  }}>
                                  {info?.addOnsData?.[index]?.[child?.id]
                                    ?.map((a) => a.description)
                                    .join(', ')}
                                </p>
                              </div>
                            ) : null}
                          </Col>
                        );
                      })}
                    </Row>
                  </div>
                ) : null}

                <div className="org-switch">
                  <div>
                    <h6 className="org-title">Select Organization</h6>
                    <ul id="infiniteScroll" style={{ maxHeight: 175 }}>
                      {results?.length > 0 &&
                        results?.map((a) => {
                          return (
                            <OrganizationCard
                              key={a.id}
                              org={a}
                              name={selectedOrg?.name}
                              onClick={(_id) => handleOrgClick(_id, a?.name)}
                            />
                          );
                        })}
                    </ul>
                  </div>
                  {showInput ? (
                    <div className="createOrgForm px-0">
                      <Form.Control
                        type="text"
                        name="orgname"
                        value={newOrgName}
                        maxLength={50}
                        onChange={(e) => setNewOrgName(e.target.value)}
                        placeholder="Enter name..."
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key !== 'Enter' || isDisabled) return;
                          handleSave?.();
                        }}
                      />
                      <div className="d-flex align-items-center mt-2 gap-2">
                        <Button disabled={isDisabled} className="cancel" onClick={handleCancel}>
                          Cancel
                        </Button>
                        <Button
                          color="warning"
                          disabled={isDisabled}
                          loading={saving}
                          className="save"
                          onClick={handleSave}>
                          Save
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <li
                      className="mt-2"
                      role="button"
                      onClick={() => {
                        if (isDisabled) {
                          return;
                        }
                        setShowInput(true);
                      }}>
                      <span className="text-action">+ Add Organization</span>
                    </li>
                  )}
                  <BigButton
                    onClick={() => onProceed(info)}
                    className={`${styles.btnColor}`}
                    disabled={packsLoading || btnLoader}
                    isLoading={btnLoader}
                    buttonTitle="Assign"
                  />
                </div>
              </PlanDetails>
            );
          })}
        </div>
      }></ModalArea>
  );
};
